/**
 * Lógica pura para las barras de llenado (peso + espacio + pallets) del bloque mixto en StoreCart.
 * Extraída para poder testear sin React y evitar regresiones.
 *
 * Modelo:
 * - Pallet: L×W fijos (constantes), H variable por producto (altura caja × capas). Tope físico
 *   por contenedor = maxPallets (no solo m³).
 * - Carga a piso: volumen = cajas × (L×W×H) caja.
 * - Espacio mixto = volumen pallets + volumen cajas. Capacidades del bloque = límite × num_containers.
 *
 * Decisión palletizado vs a piso por producto vía loading_method_id (no por block.load_type).
 */

import type { MixBlock, SourcingItem } from '@/lib/store-types';
import {
  CONTAINER_LIMITS,
  isLoadingMethodPalletized,
  PALLET_STANDARD_LENGTH_CM,
  PALLET_STANDARD_WIDTH_CM,
  PALLET_STANDARD_HEIGHT_CM,
  PALLET_MAX_HEIGHT_CM,
} from '@/lib/constants';

export { CONTAINER_LIMITS };

/** Fila del carrito usada para calcular llenado (compatible con CartLineRow de StoreCart). */
export interface MixFillRow {
  line: { sourcingItemId: string; moqUnitLabel?: string };
  quantity: number;
  unitLabel: string;
  /** User override for this line: palletized or floor. If set, overrides product loading_method. */
  rowLoadType?: 'pallets' | 'floor';
}

export interface MixFillBarsResult {
  totalWeightKg: number;
  maxWeight: number;
  pctWeight: number;
  overWeight: boolean;
  hasMissingWeight: boolean;
  totalSpaceUsed: number;
  spaceCapacity: number;
  spaceLabel: string;
  pctSpace: number;
  overSpace: boolean;
  hasMissingSpace: boolean;
  /** Total pallets in block (palletized rows: q when MOQ is pallet; boxes/boxes_per_pallet when MOQ is caja and product is palletized). Can be fractional. */
  totalPallets: number;
  /** Max pallets that fit in the block (maxPallets × num_containers). */
  palletCapacity: number;
  pctPallets: number;
  overPallets: boolean;
  /** True if any palletized row has missing boxes_per_pallet. */
  hasMissingPallets: boolean;
}

/** True when the row's unit label indicates pallets (e.g. MOQ unit "Pallet"). Uses same pattern as loading method. */
function isRowPallets(r: MixFillRow): boolean {
  return isLoadingMethodPalletized(r.unitLabel || r.line.moqUnitLabel || '');
}

/**
 * m³ per pallet: fixed L×W from constants, height default or estimated from box_height_cm and boxes_per_pallet.
 * Layers convention: layers = ceil(sqrt(bpp)) (e.g. bpp=50 → 8 layers).
 */
function m3PerPallet(item: SourcingItem | undefined): number {
  const l = PALLET_STANDARD_LENGTH_CM;
  const w = PALLET_STANDARD_WIDTH_CM;
  const boxH = item?.box_height_cm ?? 0;
  const bpp = item?.boxes_per_pallet ?? 0;
  let heightCm = PALLET_STANDARD_HEIGHT_CM;
  if (boxH > 0 && bpp > 0) {
    const layers = Math.ceil(Math.sqrt(bpp));
    heightCm = Math.min(PALLET_MAX_HEIGHT_CM, layers * boxH);
  }
  return (l * w * heightCm) / 1_000_000;
}

/** Tipo mínimo para resolver si un producto es palletizado por su loading_method. */
export type LoadingMethodLike = { id: string; name: string };

/**
 * Computes weight, volume (m³), and pallet totals for a mix block.
 * Capacities are per block fleet: limit × num_containers.
 * Pallet count is the physical cap (maxPallets × num_containers).
 */
export function computeMixFillBars(
  block: Pick<MixBlock, 'container_type' | 'load_type' | 'num_containers'>,
  sectionRows: MixFillRow[],
  allItems: SourcingItem[],
  loadingMethods: LoadingMethodLike[]
): MixFillBarsResult {
  const limits = CONTAINER_LIMITS[block.container_type];
  const numContainers = Math.max(1, block.num_containers ?? 1);
  const maxWeightTotal = limits.maxWeightKg * numContainers;
  const maxCbmTotal = limits.maxCbm * numContainers;
  const palletCapacity = limits.maxPallets * numContainers;

  let totalWeightKg = 0;
  let totalCbm = 0;
  let totalPallets = 0;
  let hasMissingWeight = false;
  let hasMissingSpace = false;
  let hasMissingPallets = false;

  for (const r of sectionRows) {
    const item = allItems.find((i) => i.id === r.line.sourcingItemId);
    const bpp = item?.boxes_per_pallet ?? 0;
    const w = item?.gross_weight_per_box_kg ?? 0;
    const rowIsPallets = isRowPallets(r);
    const q = r.quantity;
    // User override per row: if set, overrides product/unit. Otherwise use product loading_method + unit.
    const productPalletized = isLoadingMethodPalletized(
      loadingMethods.find((m) => m.id === item?.loading_method_id)?.name
    );
    const effectivePalletized =
      r.rowLoadType === 'pallets' ? true : r.rowLoadType === 'floor' ? false : rowIsPallets || productPalletized;

    const effectiveBoxes = effectivePalletized && rowIsPallets ? q * (bpp > 0 ? bpp : 1) : q;
    totalWeightKg += effectiveBoxes * w;
    if (w <= 0) hasMissingWeight = true;
    if (rowIsPallets && bpp <= 0) hasMissingWeight = true;

    // Pallet count: only rows marked as palletized (user choice or product default). Floor rows don't count.
    if (effectivePalletized) {
      if (rowIsPallets) {
        totalPallets += q;
      } else if (bpp > 0) {
        totalPallets += q / bpp;
      }
    }

    // Espacio (m³): siempre volumen físico de la mercancía (cajas × dimensiones). No depende de paletizada/a piso,
    // así el m³ no cambia al cambiar el tipo de carga; solo la barra de pallets refleja esa elección.
    const l = item?.box_length_cm ?? 0;
    const wd = item?.box_width_cm ?? 0;
    const h = item?.box_height_cm ?? 0;
    if (l > 0 && wd > 0 && h > 0) {
      const effectiveBoxesForSpace = rowIsPallets ? q * (bpp > 0 ? bpp : 1) : q;
      totalCbm += effectiveBoxesForSpace * (l * wd * h) / 1_000_000;
    } else {
      hasMissingSpace = true;
      if (effectivePalletized && !rowIsPallets && bpp <= 0) hasMissingPallets = true;
    }
  }

  const pctWeight = maxWeightTotal > 0 ? (totalWeightKg / maxWeightTotal) * 100 : 0;
  const pctSpace = maxCbmTotal > 0 ? (totalCbm / maxCbmTotal) * 100 : 0;
  const pctPallets = palletCapacity > 0 ? (totalPallets / palletCapacity) * 100 : 0;
  const overWeight = !hasMissingWeight && totalWeightKg > maxWeightTotal;
  const overSpace = !hasMissingSpace && totalCbm > maxCbmTotal;
  const overPallets = totalPallets > palletCapacity;

  return {
    totalWeightKg: Math.round(totalWeightKg),
    maxWeight: maxWeightTotal,
    pctWeight,
    overWeight,
    hasMissingWeight,
    totalSpaceUsed: totalCbm,
    spaceCapacity: maxCbmTotal,
    spaceLabel: 'm³',
    pctSpace,
    overSpace,
    hasMissingSpace,
    totalPallets, // Keep decimals: caja rows with palletized load use boxes/boxes_per_pallet
    palletCapacity,
    pctPallets,
    overPallets,
    hasMissingPallets,
  };
}
